import {
  Box,
  Flex,
  Grid,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Text,
} from '@chakra-ui/react';
import { useMixpanel } from 'gatsby-plugin-mixpanel';
import * as React from 'react';
import { links } from '../foundation/links';
import { TickDot } from '../foundation/primitives';
import { colors, fonts } from '../foundation/tokens';

// "Book a demo" opens Greg's Calendly in a Canyou-styled window instead of
// sending people off-site. Every Book a demo link points at /book-a-demo/
// (a real page with the same calendar, for new tabs and crawlers); a click
// listener in SiteShell opens this modal instead when it can.

/** The inline Calendly URL. Our own panel shows the meeting details, so
 * Calendly's are hidden; embed_domain lets Calendly tell us about bookings. */
const calendlySrc = () => {
  const params = new URLSearchParams({
    embed_type: 'Inline',
    embed_domain: typeof window === 'undefined' ? '' : window.location.host,
    hide_gdpr_banner: '1',
    hide_event_type_details: '1',
    primary_color: '2667ff',
  });
  return `${links.calendly}?${params}`;
};

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}

/** Fires once per booking made through any embedded calendar. */
const useBookingTracking = () => {
  const mixpanel = useMixpanel();
  React.useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e.origin !== 'https://calendly.com') return;
      if (e.data?.event !== 'calendly.event_scheduled') return;
      mixpanel.track('Site: Demo booked');
      window.dataLayer?.push({ event: 'demo_booked' });
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [mixpanel]);
};

export const CalendlyFrame: React.FC<{ h?: string | object }> = ({
  h = { base: '640px', md: '700px' },
}) => {
  const [src, setSrc] = React.useState('');
  const [loaded, setLoaded] = React.useState(false);
  // Built on the client so embed_domain is the real host.
  React.useEffect(() => setSrc(calendlySrc()), []);
  // Calendly draws its calendar a moment after the frame loads, and says so
  // with a message. Keep the spinner up until then.
  React.useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e.origin !== 'https://calendly.com') return;
      const ev = e.data?.event;
      if (ev === 'calendly.event_type_viewed' || ev === 'calendly.page_height')
        setLoaded(true);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);
  return (
    <Box
      position="relative"
      h={h}
      bg="white"
      borderRadius="14px"
      overflow="hidden"
    >
      {!loaded && (
        <Flex
          position="absolute"
          inset={0}
          align="center"
          justify="center"
          direction="column"
          gap="12px"
          color={colors.ink70}
          fontSize="15px"
          fontWeight={600}
        >
          <Box
            w="28px"
            h="28px"
            borderRadius="50%"
            border="3px solid"
            borderColor={colors.line}
            borderTopColor={colors.blue}
            sx={{ animation: 'cySpin .8s linear infinite' }}
          />
          Loading Greg's calendar
        </Flex>
      )}
      {src && (
        <Box
          as="iframe"
          src={src}
          title="Book a 30-minute Canyou demo"
          w="100%"
          h="100%"
          border={0}
          // Fallback in case Calendly's ready message never arrives.
          onLoad={() => window.setTimeout(() => setLoaded(true), 6000)}
          opacity={loaded ? 1 : 0}
          transition="opacity .3s"
        />
      )}
      <style>{'@keyframes cySpin{to{transform:rotate(360deg)}}'}</style>
    </Box>
  );
};

const POINTS = [
  'Your onboarding flow, set up for your crew',
  'Licence and ticket tracking to expiry',
  'Syncing with Xero, MYOB or Deputy',
  'Pricing for your team size',
];

/** What the call covers, beside the calendar. */
export const BookingDetails: React.FC<{ asH1?: boolean }> = ({ asH1 }) => (
  <Flex direction="column" gap={{ base: '10px', lg: '18px' }}>
    <Text
      m={0}
      fontSize="14px"
      fontWeight={800}
      letterSpacing=".06em"
      textTransform="uppercase"
      color={colors.blue}
    >
      30-minute demo · video call
    </Text>
    <Box
      as={asH1 ? 'h1' : 'h2'}
      m={0}
      fontFamily={fonts.display}
      fontSize={{ base: '26px', md: '38px' }}
      fontWeight={800}
      lineHeight={1.1}
      letterSpacing="-0.02em"
    >
      See Canyou with your own crew in mind
    </Box>
    {/* Phones: just the heading, so the calendar is on the first screen. */}
    <Text
      display={{ base: 'none', lg: 'block' }}
      m={0}
      fontSize="17px"
      lineHeight={1.6}
      color={colors.ink70}
    >
      Pick a time that suits you. Greg from our Australian team will walk you
      through it and answer your questions.
    </Text>
    <Flex
      as="ul"
      display={{ base: 'none', lg: 'flex' }}
      direction="column"
      gap="12px"
      m={0}
      p={0}
      listStyleType="none"
    >
      {POINTS.map((p) => (
        <Flex
          as="li"
          key={p}
          align="center"
          gap="12px"
          fontSize="16px"
          fontWeight={600}
        >
          <TickDot size={24} bg={colors.verified} />
          {p}
        </Flex>
      ))}
    </Flex>
    <Text m={0} fontSize="14px" color={colors.ink70}>
      No obligation · Australian support 🇦🇺
    </Text>
  </Flex>
);

const isDemoLink = (a: HTMLAnchorElement) => {
  if (a.href.startsWith(links.calendly)) return true;
  return a.origin === window.location.origin && a.pathname === links.demo;
};

/** Opens the booking modal for any Book a demo click. Mount once, in the
 * site shell. Cmd/ctrl-click and middle-click still open the page. */
export const DemoBookingModal: React.FC = () => {
  const [open, setOpen] = React.useState(false);
  const mixpanel = useMixpanel();
  useBookingTracking();

  React.useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = (e.target as HTMLElement).closest?.('a');
      if (!a || !isDemoLink(a)) return;
      if (window.location.pathname === links.demo) return;
      e.preventDefault();
      setOpen(true);
      mixpanel.track('Site: Opened demo booking');
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, [mixpanel]);

  return (
    <Modal
      isOpen={open}
      onClose={() => setOpen(false)}
      size={{ base: 'full', md: '6xl' }}
      scrollBehavior="inside"
      isCentered
    >
      <ModalOverlay bg="rgba(14,18,32,.55)" backdropFilter="blur(2px)" />
      <ModalContent
        className="cy-surface"
        bg={colors.white}
        color={colors.ink}
        fontFamily={fonts.body}
        border={{ base: 'none', md: '2px solid' }}
        borderColor={colors.ink}
        borderRadius={{ base: 0, md: '22px' }}
        boxShadow={{ base: 'none', md: `12px 12px 0 ${colors.blue}` }}
        overflow="hidden"
        my={{ md: '32px' }}
      >
        <ModalCloseButton
          top="14px"
          right="14px"
          size="lg"
          bg="white"
          border="2px solid"
          borderColor={colors.ink}
          borderRadius="12px"
          zIndex={2}
          aria-label="Close booking"
        />
        <ModalBody p={0}>
          <Grid templateColumns={{ base: '1fr', lg: '0.8fr 1.2fr' }}>
            <Box
              bg={colors.paper}
              borderRight={{ lg: '2px solid' }}
              borderBottom={{ base: '2px solid', lg: 'none' }}
              borderColor={colors.ink}
              p={{ base: '20px', md: '40px' }}
              pr={{ base: '64px', lg: '40px' }}
            >
              <BookingDetails />
            </Box>
            <Box p={{ base: '8px', md: '16px' }}>
              <CalendlyFrame
                h={{ base: '680px', md: 'min(700px, calc(100vh - 110px))' }}
              />
            </Box>
          </Grid>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
